import * as React from 'react';
import { Formik, FormikHelpers as FormikActions, FormikProps } from 'formik';

import { AboutCard } from './AboutCard';
import { ContactInfo, GeneralInformationFormData, TwsAdmin, WeddingPacketFileInfo } from './types';
import { defaultGeneralInfoFormData } from './constants';
import { AddressCard, LocationSuggestions } from './AddressCard';
import { StylesCard } from './StylesCard';
import { LinksCard } from './LinksCard';
import { MediaCard } from './MediaCard';
import { ContactsCard } from './ContactsCard';
import { SpinnerOverlay } from '@weddingspot/ws-core-components';
import { ContactModal } from './ContactModal';
import { anyTouched } from '../../utils/formik';

interface OwnProps {
    onSubmit: (formData: GeneralInformationFormData, formikActions: FormikActions<GeneralInformationFormData>) => Promise<void>;

    // Notify parent that an initialization error occurred
    onInitError: () => void;

    // Notify parent that a form error occurred
    onFormError: () => void;

    // Notify parent that form was successfully submitted
    onFormSuccess: () => void;

    // General
    loadingVenueData: boolean; // Whether or not we're still loading venue data
    currentVenue: TwsAdmin.AvailableVenue | null; // null means we're creating a new one
    choices: TwsAdmin.GeneralInformationChoicesData | null;
    initialValues: GeneralInformationFormData | null;
    weddingPacketFiles: WeddingPacketFileInfo[] | null;
    setTouched: (touched: boolean) => void;

    // Address card
    getLocationSuggestionsWithZipCode: (zip: string) => Promise<LocationSuggestions>;

    // Contacts card
    searchEmail: (email: string) => Promise<ContactInfo | null>;
    createNewContact: (data: ContactInfo) => Promise<ContactInfo[]>;
    updateContact: (data: ContactInfo) => Promise<ContactInfo[]>;
    deleteContact: (id: number) => Promise<ContactInfo[]>;
    sendAccountInvite: (contactInfo: ContactInfo) => Promise<void>;

    // Media card
    deleteFile: (fileId: number, formikActions: FormikActions<GeneralInformationFormData>) => Promise<void>;
}

interface State {
    contactModalShowing: boolean;
    editingContactAtIdx?: number;
}

export class GeneralInformation extends React.Component<OwnProps, State> {
    constructor(props: OwnProps) {
        super(props);

        // default state
        this.state = {
            contactModalShowing: false,
        };

        this.showContactModal = this.showContactModal.bind(this);
        this.hideContactModal = this.hideContactModal.bind(this);
        this.getEditingContact = this.getEditingContact.bind(this);
        this.handleContactFormSubmit = this.handleContactFormSubmit.bind(this);
    }

    render() {
        const { initialValues, searchEmail } = this.props;

        const buttonSpinnerProps = {
            isSpinning: !this.dataFullyLoaded(),
            spinnerProps: {
                spinnerColor: '#881155',
                spinnerDiameter: 40,
            },
        };

        const cardSpinnerProps = {
            isSpinning: !this.dataFullyLoaded(),
            spinnerProps: {
                spinnerColor: '#881155',
                spinnerDiameter: 60,
            },
        };

        return (
            <div className='VenueManagement--general'>
                <Formik
                    initialValues={initialValues || defaultGeneralInfoFormData}
                    enableReinitialize={true}
                    validate={() => {
                        const errors: Partial<GeneralInformationFormData> = {};
                        return errors;
                    }}
                    onSubmit={this.props.onSubmit}
                >
                    {(formikProps: FormikProps<GeneralInformationFormData>) => {
                        const { values, handleChange, handleSubmit, setFieldValue, touched } = formikProps;

                        // Notify parent that a field value was touched to prompt
                        // save before nav
                        this.props.setTouched(anyTouched(touched));

                        if (typeof window !== 'undefined') {
                            window.onbeforeunload = () => {
                                if (anyTouched(touched)) {
                                    // NOTE: Chromium browsers diabled custom messages
                                    // including this here in case the target browser
                                    // still supports this :(
                                    return 'You have unsaved changes! Are you sure' + ' you want to leave before saving them?';
                                }

                                return;
                            };
                        }

                        return (
                            <div>
                                <form onSubmit={handleSubmit} className='VenueManagement--general-form'>
                                    <div className='VenueManagement--body-header'>
                                        <div className='VenueManagement--body-header-title'>General information</div>
                                        {!!formikProps.status && (
                                            <div className='VenueManagement--field-level-error'>{formikProps.status}</div>
                                        )}
                                        <div className='VenueManagement--body-header--submit-container'>
                                            <SpinnerOverlay {...buttonSpinnerProps}>
                                                <button
                                                    type='submit'
                                                    className='VenueManagement--form-submit nx-button nx-button--primary'
                                                    disabled={!this.dataFullyLoaded()}
                                                >
                                                    Save
                                                </button>
                                            </SpinnerOverlay>
                                        </div>
                                    </div>
                                    <SpinnerOverlay {...cardSpinnerProps}>
                                        <AboutCard values={values} handleChange={handleChange} />
                                    </SpinnerOverlay>
                                    <SpinnerOverlay {...cardSpinnerProps}>
                                        <AddressCard
                                            formikProps={formikProps}
                                            showIntenationalFields={values.is_dw_venue}
                                            crossRegionDisabledText={'Superuser'}
                                            allStateChoices={!!this.props.choices ? this.props.choices.state.choices : []}
                                            stateMarketsMap={!!this.props.choices ? this.props.choices.state.state_market_mapping : {}}
                                            allMarketChoices={!!this.props.choices ? this.props.choices.market.choices : []}                                    
                                            marketRegionsMap={!!this.props.choices ? this.props.choices.market.market_regions_mapping : {}}
                                            allCrossMarketChoices={!!this.props.choices ? this.props.choices.cross_region_market.choices : []}                                    
                                            crossMarketRegionsMap={!!this.props.choices ? this.props.choices.cross_region_market.cross_market_regions_mapping : {}}
                                            allRegionChoices={!!this.props.choices ? this.props.choices.region_1.choices : []}
                                            getLocationSuggestionsWithZipCode={this.props.getLocationSuggestionsWithZipCode}
                                        />
                                    </SpinnerOverlay>
                                    <SpinnerOverlay {...cardSpinnerProps}>
                                        <ContactsCard
                                            disabled={!this.props.currentVenue}
                                            values={values}
                                            searchEmail={searchEmail}
                                            deleteContact={(id: number) => {
                                                return this.props.deleteContact(id).then(
                                                    // Update list w/ new contact info
                                                    (contacts: ContactInfo[]) => {
                                                        setFieldValue('contacts', contacts);
                                                    }
                                                );
                                            }}
                                            sendAccountInvite={this.props.sendAccountInvite}
                                            showContactsModal={this.showContactModal}
                                        />
                                    </SpinnerOverlay>
                                    <SpinnerOverlay {...cardSpinnerProps}>
                                        <MediaCard
                                            disabled={!this.props.currentVenue}
                                            formikProps={formikProps}
                                            photosUrl={
                                                this.props.currentVenue
                                                    ? `${window.location.protocol}//${window.location.hostname}` +
                                                      `${window.location.port ? `:${window.location.port}` : ''}/twsadmin/` +
                                                      `${this.props.currentVenue.venue_id}/manage-images/`
                                                    : '/'
                                            }
                                            existingFiles={this.props.weddingPacketFiles || []}
                                            deleteFile={this.props.deleteFile}
                                        />
                                    </SpinnerOverlay>
                                    <SpinnerOverlay {...cardSpinnerProps}>
                                        <StylesCard
                                            formikProps={formikProps}
                                            styles={!!this.props.choices ? this.props.choices.styles.choices : []}
                                        />
                                    </SpinnerOverlay>
                                    <SpinnerOverlay {...cardSpinnerProps}>
                                        <LinksCard values={values} handleChange={handleChange} />
                                    </SpinnerOverlay>
                                    <div className='VenueManagement--body-footer'>
                                        <div className='VenueManagement--body-footer--submit-container'>
                                            <SpinnerOverlay {...buttonSpinnerProps}>
                                                <button
                                                    type='submit'
                                                    className='VenueManagement--form-submit nx-button nx-button--primary'
                                                    disabled={!this.dataFullyLoaded()}
                                                >
                                                    Save
                                                </button>
                                            </SpinnerOverlay>
                                        </div>
                                    </div>
                                </form>
                                <ContactModal
                                    show={this.state.contactModalShowing}
                                    contact={this.getEditingContact(values)}
                                    handleClose={this.hideContactModal}
                                    handleSubmit={(cont) => this.handleContactFormSubmit(cont, formikProps)}
                                    findUser={this.props.searchEmail}
                                />
                            </div>
                        );
                    }}
                </Formik>
            </div>
        );
    }

    private dataFullyLoaded() {
        return !this.props.loadingVenueData && this.props.choices !== null;
    }

    private showContactModal(editingContactAtIdx?: number) {
        this.setState({
            contactModalShowing: true,
            editingContactAtIdx: editingContactAtIdx,
        });
    }

    private hideContactModal() {
        this.setState({
            contactModalShowing: false,
            editingContactAtIdx: undefined,
        });
    }

    private getEditingContact(values: GeneralInformationFormData) {
        if (this.state.editingContactAtIdx === undefined) {
            return null;
        }

        const contact = values.contacts[this.state.editingContactAtIdx];
        if (!!contact) {
            return contact;
        }

        return null;
    }

    private handleContactFormSubmit(values: ContactInfo, generalFormikActions: FormikActions<GeneralInformationFormData>) {
        if (values.id !== null) {
            return this.props.updateContact(values).then(
                // Update list w/ new contact info
                (contacts: ContactInfo[]) => {
                    generalFormikActions.setFieldValue('contacts', contacts);
                }
            );
        } else {
            return this.props.createNewContact(values).then(
                // Update list w/ new contact info
                (contacts: ContactInfo[]) => {
                    generalFormikActions.setFieldValue('contacts', contacts);
                }
            );
        }
    }
}
